import React from "react";
import { Button, Form } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { v4 as uuidv4 } from "uuid";
import { audienceBuilderStore } from "src/modules/engagement/components/phases/audience/audienceBuilderStore.ts";
import type { Entity, Field } from "src/modules/engagement/components/phases/audience/audienceBuilderStore.ts";

export interface Rule {
  id: string;
  entity: string;
  field: string;
  operator: string;
  value: any;
  // Per-dimension qualifier for an EAV metric, e.g. {"period": "2026SP"}.
  // Absent for undimensioned/relational fields (MPN-1200).
  dimension?: Record<string, string>;
  // "is one of" a catalog value group, by code (MPN-2328); the engine resolves it at run time.
  value_group?: string;
}

export interface RuleGroup {
  id: string;
  combinator: "AND" | "OR";
  rules: (Rule | RuleGroup)[];
}

const OPERATORS = {
  string: [
    { value: "equals", label: "equals" },
    { value: "not_equals", label: "does not equal" },
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "does not contain" },
    { value: "starts_with", label: "starts with" },
    { value: "ends_with", label: "ends with" },
    { value: "in", label: "is one of" },
    { value: "is_null", label: "is empty" },
    { value: "is_not_null", label: "has value" },
  ],
  number: [
    { value: "equals", label: "=" },
    { value: "not_equals", label: "≠" },
    { value: "greater_than", label: ">" },
    { value: "greater_than_equal", label: "≥" },
    { value: "less_than", label: "<" },
    { value: "less_than_equal", label: "≤" },
    { value: "between", label: "between" },
    { value: "is_null", label: "is empty" },
  ],
  date: [
    { value: "equals", label: "on" },
    { value: "greater_than", label: "after" },
    { value: "less_than", label: "before" },
    { value: "between", label: "between" },
    { value: "is_null", label: "is empty" },
  ],
  boolean: [
    { value: "equals", label: "is" },
  ],
  choice: [
    { value: "equals", label: "is" },
    { value: "not_equals", label: "is not" },
    { value: "in", label: "is one of" },
    { value: "is_null", label: "is empty" },
  ],
};

interface AudienceRuleBuilderProps {
  rules: RuleGroup;
  onChange: (rules: RuleGroup) => void;
}

export const AudienceRuleBuilder: React.FC<AudienceRuleBuilderProps> = ({
  rules,
  onChange,
}) => {
  const { entities } = useSnapshot(audienceBuilderStore);

  const addRule = (groupId: string) => {
    const newRule: Rule = {
      id: uuidv4(),
      entity: "",
      field: "",
      operator: "",
      value: null,
    };

    const updated = updatePath(rules, groupId, (group) => ({
      ...group,
      rules: [...group.rules, newRule],
    }));
    onChange(updated);
  };

  const addGroup = (parentGroupId: string) => {
    const newGroup: RuleGroup = {
      id: uuidv4(),
      combinator: "AND",
      rules: [],
    };

    const updated = updatePath(rules, parentGroupId, (group) => ({
      ...group,
      rules: [...group.rules, newGroup],
    }));
    onChange(updated);
  };

  const updateRule = (ruleId: string, updates: Partial<Rule>) => {
    const updated = updateRuleInGroup(rules, ruleId, updates);
    onChange(updated);
  };

  const updatePathCombinator = (groupId: string, combinator: "AND" | "OR") => {
    const updated = updatePath(rules, groupId, (group) => ({
      ...group,
      combinator,
    }));
    onChange(updated);
  };

  const removeRule = (ruleId: string) => {
    const updated = removeRuleFromGroup(rules, ruleId);
    onChange(updated);
  };

  // Groups carry an id just like conditions, so the same walk removes a group
  // and everything nested under it. The root is never reachable: it is returned
  // via the spread and only its children are filtered.
  const removeGroup = (groupId: string) => {
    const updated = removeRuleFromGroup(rules, groupId);
    onChange(updated);
  };

  return (
    <div className="audience-rule-builder">
      <RuleGroupComponent
        group={rules}
        level={0}
        entities={entities}
        onAddRule={addRule}
        onAddGroup={addGroup}
        onUpdateRule={updateRule}
        onUpdateCombinator={updatePathCombinator}
        onRemoveRule={removeRule}
        onRemoveGroup={removeGroup}
      />
    </div>
  );
};

interface RuleGroupComponentProps {
  group: RuleGroup;
  level: number;
  entities: readonly Entity[];
  onAddRule: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onUpdateRule: (ruleId: string, updates: Partial<Rule>) => void;
  onUpdateCombinator: (groupId: string, combinator: "AND" | "OR") => void;
  onRemoveRule: (ruleId: string) => void;
  onRemoveGroup: (groupId: string) => void;
}

const RuleGroupComponent: React.FC<RuleGroupComponentProps> = ({
  group,
  level,
  entities,
  onAddRule,
  onAddGroup,
  onUpdateRule,
  onUpdateCombinator,
  onRemoveRule,
  onRemoveGroup,
}) => {
  const isRoot = level === 0;
  const borderColor = isRoot ? "#0d6efd" : "#dee2e6";
  const backgroundColor = isRoot ? "#f0f5ff" : level === 1 ? "#ffffff" : "#fafbfc";

  return (
    <div
      className="border rounded p-3 mb-3"
      style={{
        borderColor: borderColor,
        borderWidth: isRoot ? "2px" : "1px",
        backgroundColor: backgroundColor,
        marginLeft: level === 0 ? 0 : 24,
      }}
    >
      <div className="mb-3 d-flex align-items-center">
        <span className="text-muted fw-bold me-2" style={{ fontSize: "13px" }}>
          Match
        </span>
        <Form.Select
          value={group.combinator}
          onChange={(e) => onUpdateCombinator(group.id, e.target.value as "AND" | "OR")}
          style={{ width: "80px" }}
          size="sm"
        >
          <option value="AND">ALL</option>
          <option value="OR">ANY</option>
        </Form.Select>
        <span className="text-muted ms-2" style={{ fontSize: "13px" }}>
          of the following:
        </span>
        {!isRoot && (
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-auto"
            title="Remove group"
            aria-label="Remove group"
            onClick={() => onRemoveGroup(group.id)}
          >
            ×
          </Button>
        )}
      </div>

      <div>
        {group.rules.length === 0 && (
          <div className="text-muted text-center py-3 border rounded bg-light" style={{ fontSize: "14px" }}>
            No conditions yet. Click "+ Add Condition" to start building your audience.
          </div>
        )}

        {group.rules.map((rule) => {
          if ("combinator" in rule) {
            return (
              <RuleGroupComponent
                key={rule.id}
                group={rule as RuleGroup}
                level={level + 1}
                entities={entities}
                onAddRule={onAddRule}
                onAddGroup={onAddGroup}
                onUpdateRule={onUpdateRule}
                onUpdateCombinator={onUpdateCombinator}
                onRemoveRule={onRemoveRule}
                onRemoveGroup={onRemoveGroup}
              />
            );
          }

          return (
            <RuleComponent
              key={rule.id}
              rule={rule as Rule}
              entities={entities}
              onUpdate={onUpdateRule}
              onRemove={onRemoveRule}
            />
          );
        })}
      </div>

      <hr className="my-3" />

      <div className="d-flex gap-2">
        <Button variant="outline-primary" size="sm" onClick={() => onAddRule(group.id)}>
          + Add Condition
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={() => onAddGroup(group.id)}>
          + Add Group
        </Button>
      </div>
    </div>
  );
};

interface RuleComponentProps {
  rule: Rule;
  entities: readonly Entity[];
  onUpdate: (ruleId: string, updates: Partial<Rule>) => void;
  onRemove: (ruleId: string) => void;
}

const RuleComponent: React.FC<RuleComponentProps> = ({
  rule,
  entities,
  onUpdate,
  onRemove,
}) => {
  const selectedEntity = entities.find((e) => e.name === rule.entity);
  const selectedField = selectedEntity?.fields.find((f: Field) => f.name === rule.field);
  const fieldType = selectedField?.field_type || "string";
  const operators = OPERATORS[fieldType as keyof typeof OPERATORS] || OPERATORS.string;
  const dimensionKeys = selectedField?.dimension_keys ?? [];
  const dimensionNames = selectedField?.dimension_value_names ?? {};
  const observedDimensions = selectedField?.dimensions ?? [];

  return (
    <div
      className="border rounded p-2 mb-2 d-flex align-items-center gap-2"
      style={{ backgroundColor: "#fff" }}
    >
      <Form.Select
        value={rule.entity || ""}
        onChange={(e) => onUpdate(rule.id, { entity: e.target.value, field: "", operator: "", value: null, value_group: undefined, dimension: undefined })}
        style={{ width: "180px" }}
        size="sm"
      >
        <option value="">Select entity...</option>
        {entities.map((entity) => (
          <option key={entity.name} value={entity.name}>
            {entity.display_name}
          </option>
        ))}
      </Form.Select>

      <Form.Select
        value={rule.field || ""}
        onChange={(e) => onUpdate(rule.id, { field: e.target.value, operator: "", value: null, value_group: undefined, dimension: undefined })}
        style={{ width: "180px" }}
        disabled={!rule.entity}
        size="sm"
      >
        <option value="">Select field...</option>
        {selectedEntity?.fields.map((field: Field) => (
          <option key={field.name} value={field.name}>
            {field.display_name}
          </option>
        ))}
      </Form.Select>

      {observedDimensions.length > 0 && (
        <Form.Select
          value={canonicalizeDimension(rule.dimension)}
          onChange={(e) => {
            const match = observedDimensions.find((d) => canonicalizeDimension(d) === e.target.value);
            // Blank => omit dimension entirely = "any dimension" (matches every
            // observed value AND the undimensioned cell) — never a silent 0 (MPN-1202).
            onUpdate(rule.id, { dimension: match ? { ...match } : undefined });
          }}
          title={`Dimension: ${dimensionKeys.join(", ") || "value"}`}
          // Sized to the axis names it has to show. A metric dimensioned by two axes
          // truncated its own placeholder to "Any period / f" at the old fixed 130px,
          // which reads as noise next to the identical looking dropdowns either side.
          style={{ minWidth: "130px", maxWidth: "260px", width: "auto" }}
          size="sm"
        >
          <option value="">{`Any ${dimensionKeys.join(" / ") || "value"}`}</option>
          {observedDimensions.map((dim) => {
            const canonical = canonicalizeDimension(dim);
            return (
              <option key={canonical} value={canonical}>
                {formatDimension(dim, dimensionKeys, dimensionNames)}
              </option>
            );
          })}
        </Form.Select>
      )}

      <Form.Select
        value={rule.operator || ""}
        onChange={(e) => onUpdate(rule.id, { operator: e.target.value, value: null, value_group: undefined })}
        style={{ width: "120px" }}
        disabled={!rule.field}
        size="sm"
      >
        <option value="">Operator...</option>
        {operators.map((op) => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </Form.Select>

      <ValueInput
        rule={rule}
        fieldType={fieldType}
        selectedField={selectedField}
        onUpdate={onUpdate}
      />

      <Button
        variant="outline-danger"
        size="sm"
        onClick={() => onRemove(rule.id)}
        style={{ width: "40px" }}
      >
        ×
      </Button>
    </div>
  );
};

const ValueInput: React.FC<{
  rule: Rule;
  fieldType: string;
  selectedField: Field | undefined;
  onUpdate: (ruleId: string, updates: Partial<Rule>) => void;
}> = ({ rule, fieldType, selectedField, onUpdate }) => {
  if (!rule.operator || rule.operator === "is_null" || rule.operator === "is_not_null") {
    return <Form.Control disabled placeholder="No value needed" style={{ width: "200px" }} size="sm" />;
  }

  // "is one of" a catalog value group (MPN-2328): one named list, kept in the catalog,
  // instead of codes typed here. A stored group the schema no longer offers stays
  // visible and flagged rather than silently reading as unselected.
  const groups = selectedField?.value_groups ?? [];
  if (rule.operator === "in" && (groups.length > 0 || rule.value_group)) {
    const unlisted = rule.value_group && !groups.some((g) => g.code === rule.value_group);
    return (
      <Form.Select
        value={rule.value_group ?? ""}
        isInvalid={Boolean(unlisted)}
        onChange={(e) => onUpdate(rule.id, { value_group: e.target.value || undefined, value: null })}
        title="Value group"
        style={{ width: "200px" }}
        size="sm"
      >
        <option value="">Select a group...</option>
        {unlisted && (
          <option value={rule.value_group} disabled>
            {`${rule.value_group} — not available`}
          </option>
        )}
        {groups.map((g) => (
          <option key={g.code} value={g.code}>
            {g.name}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (fieldType === "boolean") {
    return (
      <Form.Select
        value={String(rule.value)}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value === "true" })}
        style={{ width: "200px" }}
        size="sm"
      >
        <option value="">Select...</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </Form.Select>
    );
  }

  if (fieldType === "choice" && selectedField?.choices) {
    if (rule.operator === "in") {
      return (
        <Form.Select
          multiple
          value={rule.value || []}
          onChange={(e) => {
            const values = Array.from(e.target.selectedOptions, option => option.value);
            onUpdate(rule.id, { value: values });
          }}
          style={{ width: "200px", height: "80px" }}
          size="sm"
        >
          {selectedField.choices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Select
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
        style={{ width: "200px" }}
        size="sm"
      >
        <option value="">Select...</option>
        {selectedField.choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (fieldType === "date") {
    return (
      <Form.Control
        type="date"
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
        style={{ width: "200px" }}
        size="sm"
      />
    );
  }

  if (fieldType === "number") {
    return (
      <Form.Control
        type="number"
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: parseFloat(e.target.value) })}
        style={{ width: "200px" }}
        placeholder="Enter number..."
        size="sm"
      />
    );
  }

  return (
    <Form.Control
      type="text"
      value={rule.value || ""}
      onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
      style={{ width: "200px" }}
      placeholder="Enter value..."
      size="sm"
    />
  );
};

// Stable string key for a dimension dict (sorted keys) so a saved rule's
// dimension matches its picker option regardless of key order, and matches the
// backend's canonical composite key. "" == no/any dimension.
function canonicalizeDimension(dim?: Record<string, string>): string {
  if (!dim || Object.keys(dim).length === 0) return "";
  const sorted = Object.fromEntries(
    Object.entries(dim).sort(([a], [b]) => a.localeCompare(b))
  );
  return JSON.stringify(sorted);
}

// Human label for a dimension dict, values in declared key order — the catalog's
// Name for each code when it has one ("Fall 2026", or "Fall 2026 / North" for a
// multi-axis dimension), the code itself otherwise. The option's VALUE stays the
// code (MPN-1663).
function formatDimension(
  dim: Record<string, string>,
  keys: readonly string[],
  names: Record<string, Record<string, string>> = {},
): string {
  const orderedKeys = keys.length ? keys : Object.keys(dim);
  const label = orderedKeys
    .map((k) => (dim[k] != null && dim[k] !== "" ? names[k]?.[dim[k]] ?? dim[k] : dim[k])).filter((v) => v != null && v !== "").join(" / ");
  return label || JSON.stringify(dim);
}

function updatePath(
  group: RuleGroup,
  targetId: string,
  updater: (group: RuleGroup) => RuleGroup
): RuleGroup {
  if (group.id === targetId) {
    return updater(group);
  }

  return {
    ...group,
    rules: group.rules.map((rule) =>
      "combinator" in rule ? updatePath(rule as RuleGroup, targetId, updater) : rule
    ),
  };
}

function updateRuleInGroup(group: RuleGroup, ruleId: string, updates: Partial<Rule>): RuleGroup {
  return {
    ...group,
    rules: group.rules.map((rule) => {
      if ("combinator" in rule) {
        return updateRuleInGroup(rule as RuleGroup, ruleId, updates);
      }
      if (rule.id === ruleId) {
        return { ...rule, ...updates };
      }
      return rule;
    }),
  };
}

function removeRuleFromGroup(group: RuleGroup, ruleId: string): RuleGroup {
  return {
    ...group,
    rules: group.rules
      .filter((rule) => rule.id !== ruleId)
      .map((rule) =>
        "combinator" in rule ? removeRuleFromGroup(rule as RuleGroup, ruleId) : rule
      ),
  };
}